/**
 * Cinematic page-to-page warp: a fixed canvas appended to document.body (it
 * survives Next's client-side navigation) plays accent warp streaks born at
 * the trigger's position while a dark veil closes over the old page; the
 * navigation happens fully covered, then the veil lifts on the new page with
 * the streaks decelerating — one continuous shot instead of a hard route swap.
 */

function hexToRgb(hex) {
	const n = parseInt(hex.slice(1), 16);
	return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

const easeInOut = (t) => (t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2);
const easeOut = (t) => 1 - (1 - t) ** 3;

const OUT_MS = 700; // veil closes
const IN_MS = 950; // veil lifts on the destination
const MAX_MS = 6000; // hard cleanup even if navigation hangs

let active = false;

export default function playWarpTransition({ x, y, accent, navigate }) {
	if (typeof window === "undefined") return;
	const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
	if (reduced || active) {
		navigate();
		return;
	}
	active = true;

	const [r, g, b] = hexToRgb(accent);
	const canvas = document.createElement("canvas");
	canvas.setAttribute("aria-hidden", "true");
	Object.assign(canvas.style, {
		position: "fixed",
		inset: "0",
		width: "100vw",
		height: "100vh",
		zIndex: "2147483000",
		pointerEvents: "none",
	});
	document.body.appendChild(canvas);
	const ctx = canvas.getContext("2d");
	const dpr = Math.min(window.devicePixelRatio || 1, 2);
	let width = window.innerWidth;
	let height = window.innerHeight;
	canvas.width = Math.round(width * dpr);
	canvas.height = Math.round(height * dpr);
	ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

	const maxDist = Math.hypot(
		Math.max(x, width - x),
		Math.max(y, height - y),
	);

	const streaks = [];
	const spawn = (n, fast) => {
		for (let i = 0; i < n; i++) {
			streaks.push({
				a: Math.random() * Math.PI * 2,
				d: fast ? Math.random() * 60 : 0,
				v: 3.5 + Math.random() * 3,
				len: 60 + Math.random() * 130,
				w: 0.8 + Math.random() * 1.8,
				alpha: 0.5 + Math.random() * 0.5,
			});
		}
	};
	spawn(64, false);

	let phase = "out"; // out -> hold -> in
	let phaseStart = performance.now();
	let navigated = false;
	let rafId = null;

	const cleanup = () => {
		clearTimeout(navTimer);
		clearTimeout(killTimer);
		if (rafId) cancelAnimationFrame(rafId);
		canvas.remove();
		active = false;
	};

	const startIn = () => {
		if (phase === "in") return;
		phase = "in";
		phaseStart = performance.now();
	};

	const triggerNavigate = () => {
		if (navigated) return;
		navigated = true;
		phase = "hold";
		phaseStart = performance.now();
		Promise.resolve(navigate())
			.catch(() => {})
			// one settle frame so the destination paints under the veil
			.then(() => setTimeout(startIn, 90));
	};

	// rAF can be throttled to a halt (occluded window, background tab); these
	// wall-clock guards make sure the navigation still fires and the overlay
	// never outlives the transition
	const navTimer = setTimeout(triggerNavigate, OUT_MS + 300);
	const killTimer = setTimeout(cleanup, MAX_MS);

	function frame(now) {
		const t = (now - phaseStart) / (phase === "in" ? IN_MS : OUT_MS);

		ctx.clearRect(0, 0, width, height);

		// dark veil
		let veil;
		if (phase === "out") veil = easeInOut(Math.min(1, t * 1.25));
		else if (phase === "hold") veil = 1;
		else veil = 1 - easeInOut(Math.min(1, t));
		ctx.fillStyle = `rgba(0,1,4,${veil.toFixed(3)})`;
		ctx.fillRect(0, 0, width, height);

		// accent flash blooming from the trigger while the veil closes
		if (phase === "out") {
			const flash = (1 - Math.min(1, t)) * 0.5;
			const radius = 90 + easeOut(Math.min(1, t)) * maxDist * 0.6;
			const glow = ctx.createRadialGradient(x, y, 0, x, y, radius);
			glow.addColorStop(0, `rgba(${r},${g},${b},${flash.toFixed(3)})`);
			glow.addColorStop(1, `rgba(${r},${g},${b},0)`);
			ctx.fillStyle = glow;
			ctx.fillRect(0, 0, width, height);
		}

		// warp streaks: accelerate while leaving, decelerate while arriving
		ctx.globalCompositeOperation = "lighter";
		if (phase === "out" && streaks.length < 150) spawn(4, true);
		const dim = phase === "in" ? 1 - easeInOut(Math.min(1, t)) : 1;
		for (let i = streaks.length - 1; i >= 0; i--) {
			const s = streaks[i];
			s.v = phase === "in" ? s.v * 0.965 : s.v * 1.05;
			s.d += s.v;
			if (s.d - s.len > maxDist) {
				streaks.splice(i, 1);
				continue;
			}
			const hx = x + Math.cos(s.a) * s.d;
			const hy = y + Math.sin(s.a) * s.d;
			const tx = x + Math.cos(s.a) * Math.max(0, s.d - s.len);
			const ty = y + Math.sin(s.a) * Math.max(0, s.d - s.len);
			const lg = ctx.createLinearGradient(tx, ty, hx, hy);
			lg.addColorStop(0, `rgba(${r},${g},${b},0)`);
			lg.addColorStop(1, `rgba(${r},${g},${b},${(s.alpha * dim).toFixed(3)})`);
			ctx.strokeStyle = lg;
			ctx.lineWidth = s.w;
			ctx.beginPath();
			ctx.moveTo(tx, ty);
			ctx.lineTo(hx, hy);
			ctx.stroke();
			ctx.fillStyle = `rgba(255,255,255,${(0.55 * dim).toFixed(3)})`;
			ctx.fillRect(hx - 1, hy - 1, 2, 2);
		}
		ctx.globalCompositeOperation = "source-over";

		if (phase === "out" && t >= 1) triggerNavigate();

		if (phase === "in" && t >= 1) {
			cleanup();
			return;
		}
		rafId = requestAnimationFrame(frame);
	}

	rafId = requestAnimationFrame(frame);
}
